import { useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import { WriteDialog } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/WriteDialog';
import {
  BATCH_LIMIT,
  needsLineChoice,
  useGetMoveDestinationsQuery,
  useGetSeatDetailQuery,
  type MoveDestination,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Button, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import type { ReportRow } from './batchReport';
import { knownSeats, movableSeats, type SeatHolder } from './memberSeats';
import { membershipConsequence } from './seatConsequence';
import {
  BatchReport,
  destinationScopedName,
  DestinationPicker,
  LinePicker,
  SeatConsequence,
  SeatPicker,
} from './SeatDialogParts';

const { seatActions } = appStrings.modules.coachingDashboard;
const strings = seatActions.moveDialog;

/** What one submit produced: the rows a batch reported, or an empty list where one seat
 * moved, plus what the whole batch cost. */
export type MoveOutcome = Readonly<{
  ok: boolean;
  message: string | null;
  rows: readonly ReportRow[];
  isOverCap: boolean;
  cohortsRemoved: number | null;
}>;

export type MoveDoors = Readonly<{
  isMoving: boolean;
  onMove: (args: {
    seatIds: readonly number[];
    /** The origin roster, for the single door's nested route. Null for a batch, which
     * spans rosters and is not nested under one. */
    sourceRosterId: number | null;
    destinationRosterId: number;
    destinationName: string;
    sourceGrantId: number | null;
  }) => Promise<MoveOutcome>;
}>;

type MoveSeatsDialogViewProps = Readonly<{
  open: boolean;
  onClose: () => void;
  holders: readonly SeatHolder[];
  doors: MoveDoors;
}>;

const flatten = (groups: readonly { rosters: readonly MoveDestination[] }[]) =>
  groups.flatMap((group) => group.rosters);

export const MoveSeatsDialogView = ({
  open,
  onClose,
  holders,
  doors,
}: MoveSeatsDialogViewProps) => {
  const isSingle = holders.length === 1;
  const ownSeats = isSingle ? movableSeats(knownSeats(holders[0].seats)) : [];
  const [seatId, setSeatId] = useState<number | null>(null);
  const [destinationRosterId, setDestinationRosterId] = useState<number | null>(null);
  const [grantId, setGrantId] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // One awaited answer held whole: the count, the cap warning and the cohort cost are
  // written together or not at all, and a partial write has no meaning.
  const [outcome, setOutcome] = useState<MoveOutcome | null>(null);

  const batchSeats = holders.flatMap((holder) => movableSeats(knownSeats(holder.seats)));
  const selectedSeat = isSingle
    ? (ownSeats.find((seat) => seat.seat_id === seatId) ?? ownSeats[0])
    : undefined;
  // A batch asks the seatless form. Probing with one seat would exclude THAT seat's own
  // roster for every other seat in the selection, hiding a legal destination from all of
  // them; the per-item already-on-destination refusals absorb the noise instead.
  const destinationArgs = isSingle
    ? selectedSeat === undefined
      ? null
      : { seatId: selectedSeat.seat_id }
    : { seatId: null };

  useEffect(() => {
    if (!open) return;

    setSeatId(null);
    setDestinationRosterId(null);
    setGrantId(null);
    setRefusal(null);
    setOutcome(null);
  }, [open]);

  const destinations = useGetMoveDestinationsQuery(
    open && destinationArgs !== null ? destinationArgs : skipToken,
  );

  // Named for one member only: a batch would have to read every seat, and its per-item
  // report says what each move actually took with it.
  const detail = useGetSeatDetailQuery(
    open && isSingle && selectedSeat !== undefined
      ? { rosterId: selectedSeat.roster_id, seatId: selectedSeat.seat_id }
      : skipToken,
  );

  const groups = destinations.data ?? [];
  const destination = flatten(groups).find((row) => row.id === destinationRosterId) ?? null;
  const lines = destination?.lines ?? [];
  const needsLine = destination !== null && needsLineChoice(destination);
  // The consequence states what a move costs. Where none is on offer — withheld, still
  // reading, or refused — it prices something that cannot happen, beside the reason it
  // cannot.
  const isMoveOffered = groups.length > 0;
  const pendingSeatIds = isSingle
    ? selectedSeat === undefined
      ? []
      : [selectedSeat.seat_id]
    : batchSeats.map((seat) => seat.seat_id);
  const isOverLimit = pendingSeatIds.length > BATCH_LIMIT;

  const submit = async () => {
    const seatIds = pendingSeatIds;
    // Only the single door names an origin. Read as a number or not at all — a roster id
    // that is absent for any reason must not become a path segment.
    const sourceRosterId =
      isSingle && Number.isInteger(selectedSeat?.roster_id)
        ? Number(selectedSeat?.roster_id)
        : null;

    if (destination === null || seatIds.length === 0) return;
    if (isSingle && sourceRosterId === null) return;

    setRefusal(null);

    const result = await doors.onMove({
      seatIds,
      sourceRosterId,
      destinationRosterId: destination.id,
      destinationName: destinationScopedName(destination),
      sourceGrantId: needsLine ? grantId : null,
    });

    setOutcome(result);

    if (result.ok && result.rows.length === 0) {
      onClose();

      return;
    }

    if (!result.ok) setRefusal(noticeForError(result.message, strings.failed));
  };

  // A rendered report closes the door: each item moved in its own transaction, so a
  // second submit re-runs the ones that already landed.
  const rows = outcome?.rows ?? [];
  const isSubmittable =
    destination !== null &&
    !doors.isMoving &&
    !isOverLimit &&
    rows.length === 0 &&
    (!needsLine || grantId !== null);

  return (
    <WriteDialog
      id="coaching-dashboard-move-seat-dialog"
      open={open}
      onClose={onClose}
      title={isSingle ? strings.title(holders[0].name) : strings.bulkTitle(holders.length)}
      footer={
        <>
          <Button
            id="coaching-dashboard-move-seat-cancel"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClose}
          >
            {rows.length > 0 ? seatActions.close : seatActions.cancel}
          </Button>
          <Button
            id="coaching-dashboard-move-seat-submit"
            hierarchy="Primary"
            size="md"
            disabled={!isSubmittable}
            onClick={() => void submit()}
          >
            {doors.isMoving ? strings.submitting : isSingle ? strings.submit : strings.bulkSubmit}
          </Button>
        </>
      }
    >
      {isOverLimit ? (
        <Typography
          id="coaching-dashboard-move-seat-over-limit"
          variant="text-sm-regular"
          role="alert"
        >
          {seatActions.withheld.overBatchLimit(BATCH_LIMIT)}
        </Typography>
      ) : null}
      {refusal !== null ? (
        <Typography
          id="coaching-dashboard-move-seat-refusal"
          variant="text-sm-regular"
          role="alert"
        >
          {refusal}
        </Typography>
      ) : null}
      <SeatPicker
        id="coaching-dashboard-move-seat-picker"
        seats={ownSeats}
        selectedSeatId={selectedSeat?.seat_id ?? null}
        onChange={setSeatId}
      />
      <DestinationPicker
        id="coaching-dashboard-move-seat-destination"
        groups={groups}
        selectedRosterId={destinationRosterId}
        onChange={(next) => {
          setDestinationRosterId(next);
          setGrantId(null);
        }}
        isLoading={destinations.isFetching}
        isError={destinations.isError}
        isUnasked={destinationArgs === null}
        errorMessage={refusalProse(destinations.error)}
      />
      {isSingle && isMoveOffered ? (
        <SeatConsequence
          id="coaching-dashboard-move-seat-consequence"
          source={membershipConsequence(detail.data, detail.isFetching, detail.isError)}
          isCoachSeat={false}
        />
      ) : null}
      <LinePicker
        id="coaching-dashboard-move-seat-line"
        lines={lines}
        selectedGrantId={grantId}
        onChange={setGrantId}
      />
      <BatchReport
        id="coaching-dashboard-move-seat-report"
        rows={rows}
        isOverCap={outcome?.isOverCap ?? false}
        cohortsRemoved={outcome?.cohortsRemoved ?? null}
      />
    </WriteDialog>
  );
};
